/**
 * High-level MLS wrapper over `ts-mls`.
 *
 * This layer keeps the common "two people talking in a group" flow short:
 * create a KeyPackage, create a group, add a member, exchange messages.
 * Advanced operations (external joins, reinit, PSK) remain available by
 * importing `ts-mls` directly.
 *
 * Default cipher suite: `MLS_128_DHKEMX25519_AES128GCM_SHA256_Ed25519` — uses
 * only Node/WebCrypto primitives, no extra dependencies.
 */
import {
  type CiphersuiteImpl,
  type CiphersuiteName,
  type ClientState,
  type Credential,
  type KeyPackage,
  type MLSMessage,
  type PrivateKeyPackage,
  type Proposal,
  type Welcome,
  createApplicationMessage,
  createCommit,
  createGroup as mlsCreateGroup,
  decodeMlsMessage,
  defaultCapabilities,
  defaultLifetime,
  emptyPskIndex,
  encodeMlsMessage,
  generateKeyPackage,
  getCiphersuiteFromName,
  getCiphersuiteImpl,
  joinGroup,
  processPrivateMessage,
  zeroOutUint8Array,
} from 'ts-mls';

export const DEFAULT_CIPHERSUITE: CiphersuiteName = 'MLS_128_DHKEMX25519_AES128GCM_SHA256_Ed25519';

let cachedDefaultSuite: Promise<CiphersuiteImpl> | undefined;

/**
 * Resolves a ciphersuite implementation. The default is cached because
 * ciphersuite setup is non-trivial and the default is the common case.
 */
export async function resolveCiphersuite(
  name: CiphersuiteName = DEFAULT_CIPHERSUITE,
): Promise<CiphersuiteImpl> {
  if (name === DEFAULT_CIPHERSUITE) {
    cachedDefaultSuite ??= getCiphersuiteImpl(getCiphersuiteFromName(DEFAULT_CIPHERSUITE));
    return cachedDefaultSuite;
  }
  return getCiphersuiteImpl(getCiphersuiteFromName(name));
}

export interface KeyPackageBundle {
  readonly publicPackage: KeyPackage;
  readonly privatePackage: PrivateKeyPackage;
}

/**
 * Creates a fresh KeyPackage for `identity`. Each KeyPackage is intended for a
 * single group-join — rotate after use.
 */
export async function createKeyPackage(
  identity: string,
  suite: CiphersuiteImpl,
): Promise<KeyPackageBundle> {
  const credential: Credential = {
    credentialType: 'basic',
    identity: new TextEncoder().encode(identity),
  };
  return generateKeyPackage(credential, defaultCapabilities(), defaultLifetime, [], suite);
}

/**
 * Creates a new MLS group owned by the bundle holder.
 */
export async function createGroup(
  groupId: Uint8Array,
  bundle: KeyPackageBundle,
  suite: CiphersuiteImpl,
): Promise<ClientState> {
  return mlsCreateGroup(groupId, bundle.publicPackage, bundle.privatePackage, [], suite);
}

export interface AddMemberResult {
  readonly newState: ClientState;
  /** Encoded MLS Welcome message — hand to the invitee out of band. */
  readonly welcome: Uint8Array;
  /**
   * Encoded MLS Commit message — broadcast to the existing group over
   * the regular application channel so every other member advances to
   * the new epoch. Without this, older members' ClientStates stay at
   * the prior epoch and any subsequent `encryptMessage` from the
   * inviter fails to decrypt on their side.
   */
  readonly commit: Uint8Array;
}

/**
 * Proposes + commits adding `theirKeyPackage` to the group. Returns the
 * new state for the inviter and the encoded Welcome message to hand to
 * the invitee out-of-band.
 */
export async function addMember(
  state: ClientState,
  theirKeyPackage: KeyPackage,
  suite: CiphersuiteImpl,
): Promise<AddMemberResult> {
  const proposal: Proposal = {
    proposalType: 'add',
    add: { keyPackage: theirKeyPackage },
  };
  const result = await createCommit({ state, cipherSuite: suite }, { extraProposals: [proposal] });
  result.consumed.forEach(zeroOutUint8Array);
  if (result.welcome === undefined) {
    throw new Error('expected commit to produce a welcome');
  }
  const encodedWelcome = encodeMlsMessage({
    welcome: result.welcome,
    wireformat: 'mls_welcome',
    version: 'mls10',
  });
  const encodedCommit = encodeMlsMessage(result.commit);
  return { newState: result.newState, welcome: encodedWelcome, commit: encodedCommit };
}

export interface RemoveMemberResult {
  readonly newState: ClientState;
  /**
   * Encoded MLS Commit message — broadcast to the remaining group
   * members so they advance to the new (post-removal) epoch. The
   * removed member can also process this commit to learn they were
   * kicked, but won't be able to encrypt or decrypt anything in the
   * new epoch.
   */
  readonly commit: Uint8Array;
}

/**
 * Proposes + commits removing the leaf at `leafIndex` from the
 * group. Caller obtains the leaf index via `memberLeafIndex` after
 * matching the target's userId in the ratchet tree.
 */
export async function removeMember(
  state: ClientState,
  leafIndex: number,
  suite: CiphersuiteImpl,
): Promise<RemoveMemberResult> {
  const proposal: Proposal = {
    proposalType: 'remove',
    remove: { removed: leafIndex },
  };
  const result = await createCommit({ state, cipherSuite: suite }, { extraProposals: [proposal] });
  result.consumed.forEach(zeroOutUint8Array);
  return { newState: result.newState, commit: encodeMlsMessage(result.commit) };
}

/**
 * Walk the ratchet tree and return the leaf index whose basic-
 * credential identity matches `userId`. Returns -1 if not found —
 * either the user isn't a member, or their leaf is blank (already
 * removed).
 */
export function memberLeafIndex(state: ClientState, userId: string): number {
  const dec = new TextDecoder();
  for (let i = 0; i < state.ratchetTree.length; i++) {
    const node = state.ratchetTree[i];
    if (!node || node.nodeType !== 'leaf') continue;
    const cred = node.leaf.credential;
    if (cred.credentialType !== 'basic') continue;
    try {
      if (dec.decode(cred.identity) === userId) return i;
    } catch {
      // skip invalid utf-8 — shouldn't happen for our own creds
    }
  }
  return -1;
}

type WireTag = MLSMessage['wireformat'];

/**
 * Decodes a serialized MLS message and asserts its wireformat.
 * `ts-mls`'s codec returns a tagged union; this keeps narrowing local.
 */
export function decodeAs<Tag extends WireTag>(
  bytes: Uint8Array,
  tag: Tag,
): Extract<MLSMessage, { wireformat: Tag }> | undefined {
  const decoded = decodeMlsMessage(bytes, 0);
  if (!decoded) return undefined;
  const message = decoded[0];
  if (message.wireformat !== tag) return undefined;
  return message as Extract<MLSMessage, { wireformat: Tag }>;
}

export function encodeWelcome(welcome: Welcome): Uint8Array {
  return encodeMlsMessage({ welcome, wireformat: 'mls_welcome', version: 'mls10' });
}

/**
 * Enumerate the `basic` credential identities (our userIds) present in a
 * ClientState's ratchet tree. Blank leaves (removed members) are skipped.
 * Non-basic credential types fall through silently.
 */
export function memberIdentities(state: ClientState): string[] {
  const out: string[] = [];
  const dec = new TextDecoder();
  for (const node of state.ratchetTree) {
    if (!node || node.nodeType !== 'leaf') continue;
    const cred = node.leaf.credential;
    if (cred.credentialType !== 'basic') continue;
    try {
      out.push(dec.decode(cred.identity));
    } catch {
      // skip invalid utf-8 — shouldn't happen for our own-written creds
    }
  }
  return out;
}

export function encodeKeyPackageMessage(keyPackage: KeyPackage): Uint8Array {
  return encodeMlsMessage({
    keyPackage,
    wireformat: 'mls_key_package',
    version: 'mls10',
  });
}

/**
 * Accept an invite from an encoded Welcome. The joiner must pass the ratchet
 * tree they received out-of-band (today we return it alongside the welcome;
 * in Phase 3 the server will include it as a GroupInfo extension).
 */
export async function acceptWelcome(
  encodedWelcome: Uint8Array,
  bundle: KeyPackageBundle,
  ratchetTree: ClientState['ratchetTree'],
  suite: CiphersuiteImpl,
): Promise<ClientState> {
  const decoded = decodeAs(encodedWelcome, 'mls_welcome');
  if (!decoded) throw new Error('invalid welcome message');
  return joinGroup(
    decoded.welcome,
    bundle.publicPackage,
    bundle.privatePackage,
    emptyPskIndex,
    suite,
    ratchetTree,
  );
}

export interface EncryptResult {
  readonly newState: ClientState;
  readonly ciphertext: Uint8Array;
}

/**
 * Encrypts an application-layer message for the group.
 */
export async function encryptMessage(
  state: ClientState,
  plaintext: Uint8Array,
  suite: CiphersuiteImpl,
): Promise<EncryptResult> {
  const result = await createApplicationMessage(state, plaintext, suite);
  result.consumed.forEach(zeroOutUint8Array);
  const ciphertext = encodeMlsMessage({
    privateMessage: result.privateMessage,
    wireformat: 'mls_private_message',
    version: 'mls10',
  });
  return { newState: result.newState, ciphertext };
}

export interface DecryptResult {
  readonly newState: ClientState;
  readonly plaintext: Uint8Array;
}

/**
 * Process an incoming MLS private message. Two possibilities:
 *   - `applicationMessage` — normal chat plaintext. `plaintext` is set,
 *     `handshake` is false.
 *   - handshake (commit / proposal) — advances the ClientState's
 *     cryptographic position without surfacing any plaintext.
 *     `plaintext` is undefined, `handshake` is true.
 *
 * The caller persists `newState` either way; it's safe to discard a
 * handshake-only result for the UI, but the state MUST be stored or the
 * receiver will miss subsequent app messages in the new epoch.
 */
export async function decryptMessage(
  state: ClientState,
  ciphertext: Uint8Array,
  suite: CiphersuiteImpl,
): Promise<DecryptResult & { handshake: boolean }> {
  const decoded = decodeAs(ciphertext, 'mls_private_message');
  if (!decoded) throw new Error('invalid private message');
  const result = await processPrivateMessage(state, decoded.privateMessage, emptyPskIndex, suite);
  result.consumed.forEach(zeroOutUint8Array);
  if (result.kind === 'applicationMessage') {
    return { newState: result.newState, plaintext: result.message, handshake: false };
  }
  // Commit / proposal / external-init → the ratchet advanced, no
  // plaintext to surface. Return an empty byte array as plaintext so
  // the return shape stays uniform.
  return { newState: result.newState, plaintext: new Uint8Array(0), handshake: true };
}
